import { Box } from '@mui/material';
import clsx from 'clsx';
import { ReactNode } from 'react';

import { Skeleton } from '../../../components/feedback/skeleton';
import { tokens } from '../../../theme/generated/tokens';
import { SxProps, WithRef } from '../../../types';
import { mergeSx } from '../../../utils';

import { formRowLayoutClassNames } from './classNames';

export type FormRowLayoutProps = {
  /** Left column content — typically a label block (title + description). */
  startColumn: ReactNode;
  /** Right column content — the control(s) for this row. */
  endColumn: ReactNode;
  /** Renders a divider above the row. */
  topDivider?: boolean;
  /** Renders a divider below the row. On by default — pass false for the last row of a list. */
  bottomDivider?: boolean;
  /** Swaps `endColumn` for a skeleton placeholder while loading. */
  isLoading?: boolean;
  /** Additional CSS class */
  className?: string;
  /** MUI sx prop */
  sx?: SxProps;
  /** `data-testid` attribute value */
  testId?: string;
};

const DIVIDER_BORDER = `1px solid ${tokens.theme.border.structural.neutralSubtle}`;

/**
 * Bare two-column form-row layout: a fixed-width start column (label block)
 * and an auto-sized end column (controls). The composition layer (FormRow)
 * decides content; this component only provides the grid, the optional
 * dividers, and the loading placeholder.
 */
export const FormRowLayout = ({
  ref,
  startColumn,
  endColumn,
  topDivider,
  bottomDivider = true,
  isLoading,
  className,
  sx,
  testId,
}: WithRef<HTMLDivElement, FormRowLayoutProps>) => {
  return (
    <Box
      ref={ref}
      data-testid={testId}
      className={clsx(
        formRowLayoutClassNames.root,
        topDivider && formRowLayoutClassNames.topDivider,
        bottomDivider && formRowLayoutClassNames.bottomDivider,
        isLoading && formRowLayoutClassNames.loading,
        className
      )}
      sx={mergeSx(
        {
          display: 'grid',
          gridTemplateColumns: '276px auto',
          gap: '60px',
          py: '20px',
          borderTop: topDivider ? DIVIDER_BORDER : 'none',
          borderBottom: bottomDivider ? DIVIDER_BORDER : 'none',
        },
        sx
      )}
    >
      <Box className={formRowLayoutClassNames.startColumn}>{startColumn}</Box>
      <Box
        className={formRowLayoutClassNames.endColumn}
        sx={{ display: 'flex', flexDirection: 'column' }}
      >
        {isLoading ? <Skeleton height={35} /> : endColumn}
      </Box>
    </Box>
  );
};

FormRowLayout.displayName = 'FormRowLayout';
